import React, { useState, useEffect } from 'react';
import { Input, Row, Col, Card, Alert, Space, Tag } from 'antd';

/**
 * RegexContextTransform - Compact inline component for regex transformation in context manager
 * Shows source key, pattern, replacement, target key in a single row with test button
 */
const RegexContextTransform = ({ 
  sourceKey, 
  pattern, 
  replacement, 
  targetKey,
  flags,
  onSourceKeyChange,
  onPatternChange,
  onReplacementChange,
  onTargetKeyChange
}) => {
  const [testInput, setTestInput] = useState('');
  const [testOutput, setTestOutput] = useState('');
  const [testError, setTestError] = useState('');

  // Auto-test when pattern or input changes
  useEffect(() => {
    if (testInput && pattern) {
      testRegex();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testInput, pattern, replacement, flags]);

  const testRegex = () => {
    if (!testInput || !pattern) {
      setTestOutput('');
      setTestError('');
      return;
    }

    try {
      // Build regex flags
      let regexFlags = '';
      if (flags && Array.isArray(flags)) {
        if (flags.includes('IGNORECASE')) regexFlags += 'i';
        if (flags.includes('MULTILINE')) regexFlags += 'm';
        if (flags.includes('DOTALL')) regexFlags += 's';
      }

      const regex = new RegExp(pattern, regexFlags);
      
      if (replacement) {
        // Use replace for transformation
        const result = testInput.replace(regex, replacement);
        setTestOutput(result);
        setTestError('');
      } else {
        // Use match for extraction
        const match = regex.exec(testInput);
        if (match) {
          const result = match[1] || match[0];
          setTestOutput(result);
          setTestError('');
        } else {
          setTestOutput('');
          setTestError('No match found');
        }
      }
    } catch (error) {
      setTestOutput('');
      setTestError(`Invalid regex: ${error.message}`);
    }
  };

  return (
    <div>
      {/* Main Configuration Row */}
      <Row gutter={8} style={{ marginBottom: 8 }}>
        <Col span={6}>
          <Input
            placeholder="source_key"
            value={sourceKey}
            onChange={(e) => onSourceKeyChange(e.target.value)}
            size="small"
            addonBefore="From"
            style={{ fontSize: '12px' }}
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="^(.+)\\.pdf$"
            value={pattern}
            onChange={(e) => onPatternChange(e.target.value)}
            size="small"
            addonBefore="Pattern"
            style={{ fontSize: '12px', fontFamily: 'monospace' }}
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="\\1_processed.pdf"
            value={replacement}
            onChange={(e) => onReplacementChange(e.target.value)}
            size="small"
            addonBefore="Replace"
            style={{ fontSize: '12px', fontFamily: 'monospace' }}
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="target_key"
            value={targetKey}
            onChange={(e) => onTargetKeyChange(e.target.value)}
            size="small"
            addonBefore="To"
            style={{ fontSize: '12px' }}
          />
        </Col>
      </Row>

      {/* Test Section */}
      <Card 
        size="small" 
        style={{ 
          background: '#fafafa',
          marginTop: 8
        }}
        bodyStyle={{ padding: '8px 12px' }}
      >
        <Row gutter={8} align="middle">
          <Col span={2}>
            <span style={{ fontSize: '11px', color: '#666', fontWeight: 500 }}>TEST:</span>
          </Col>
          <Col span={9}>
            <Input
              placeholder="Enter test string (e.g., document.pdf)"
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              size="small"
              style={{ fontSize: '12px' }}
              prefix={<span style={{ fontSize: '11px', color: '#999' }}>Input:</span>}
            />
          </Col>
          <Col span={1} style={{ textAlign: 'center' }}>
            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>→</span>
          </Col>
          <Col span={12}>
            {testError ? (
              <Alert 
                message={testError} 
                type="error" 
                showIcon 
                style={{ fontSize: '11px', padding: '2px 8px' }}
              />
            ) : testOutput ? (
              <div style={{ 
                padding: '4px 8px', 
                background: '#f6ffed', 
                border: '1px solid #b7eb8f',
                borderRadius: '2px',
                fontSize: '12px',
                fontFamily: 'monospace'
              }}>
                <span style={{ fontSize: '11px', color: '#52c41a', marginRight: 8 }}>✓ Output:</span>
                <strong>{testOutput}</strong>
              </div>
            ) : testInput && (
              <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                Enter pattern to see result
              </span>
            )}
          </Col>
        </Row>

        {/* Common Examples Helper */}
        {!testInput && (
          <div style={{ marginTop: 8, fontSize: '11px', color: '#666' }}>
            <Space size={4} wrap>
              <Tag 
                color="blue" 
                style={{ cursor: 'pointer', fontSize: '10px' }}
                onClick={() => setTestInput('document.pdf')}
              >
                document.pdf
              </Tag>
              <Tag 
                color="blue" 
                style={{ cursor: 'pointer', fontSize: '10px' }}
                onClick={() => setTestInput('sales_report_2024-12-03.csv')}
              >
                sales_report_2024-12-03.csv
              </Tag>
              <Tag 
                color="blue" 
                style={{ cursor: 'pointer', fontSize: '10px' }}
                onClick={() => setTestInput('image_001.jpg')}
              >
                image_001.jpg
              </Tag>
            </Space>
          </div>
        )}
      </Card>

      {/* Quick Tips */}
      <div style={{ marginTop: 8, fontSize: '11px', color: '#999', lineHeight: '1.4' }}>
        <strong>Tips:</strong> Use capture groups () in pattern, reference with \1, \2 in replacement. 
        Leave replacement empty to extract match.
      </div>
    </div>
  );
};

export default RegexContextTransform;
